import DateAvailability from '../models/DateAvailability'
import Booking from '../models/Booking'
import AvailabilityProvider from './AvailabilityProvider'
import { Source } from '../models/Source'
import DateService from './DateService'

class DateAvailabilityCache {
    data: {[index: number]: DateAvailability[]} = {}

    hasMonth (date: Date): boolean {
        const month = DateService.getStartOfMonth(date)
        const monthString = DateService.formatDateForCache(month)

        return this.data[monthString] !== undefined
    }

    getMonth (date: Date): DateAvailability[] | null {
        const month = DateService.getStartOfMonth(date)
        const monthString = DateService.formatDateForCache(month)

        return this.data[monthString]
    }

    getDate (date: Date): DateAvailability | null {
        date = DateService.getStartOfDay(date)
        const monthData = this.getMonth(date)

        let availabilityForDate: DateAvailability | null = null

        if (!monthData) {
            return availabilityForDate
        }

        monthData.forEach((dateAvailability) => {
            if (DateService.formatDateForCache(dateAvailability.date) === DateService.formatDateForCache(date)) {
                return availabilityForDate = dateAvailability
            }
        })

        return availabilityForDate
    }

    addDataForMonth (date: Date, data: DateAvailability[]): this {
        const monthString = DateService.formatDateForCache(date)

        this.data[monthString] = data

        return this
    }
}

export class UnavailableDate {
    private from: Date
    private to: Date
    message: string | null

    static isValidUnavailableDate (data: any) {
        const date = new UnavailableDate(data)

        return !isNaN(date.from.getTime()) && !isNaN(date.to.getTime())
    }

    isMoreThanOneMonthInFuture (): boolean {
        const unavailableEnding = DateService.getStartOfMonth(this.to)
        const startOfMonth = DateService.getStartOfMonth(new Date())

        return unavailableEnding > startOfMonth
    }

    constructor (data: any) {
        this.from = new Date(data.from)
        this.to = new Date(data.to)
        this.message = data.unavailable_message
    }

    get nextAvailableDate (): Date {
        return this.to
    }
}

export default class DateAvailabilityProvider extends AvailabilityProvider {    
    cache: DateAvailabilityCache = new DateAvailabilityCache()

    constructor (enquiry: Booking, cache: DateAvailabilityCache | undefined) {
        super(enquiry)

        if (cache instanceof DateAvailabilityCache) {
            this.cache = cache
        }
    }

    resetCache () {
        this.cache = new DateAvailabilityCache()
    }

    findAvailabilityForMonth (date: Date): DateAvailability[] {
        const month = DateService.getStartOfMonth(date)

        return this.cache.getMonth(month) || []
    }

    hasAvailabilityForMonth (date: Date): boolean {
        return this.cache.hasMonth(date)
    }

    findAvailabilityForDate (date: Date): DateAvailability | null {
        return this.cache.getDate(date)
    }

    isDateAllowed (date: Date): boolean {
        const availabilityForDate = this.findAvailabilityForDate(date)

        return availabilityForDate ? availabilityForDate.valid : false
    }

    checkAvailabilityForMonth(date: Date): Promise<any> {
        const month = DateService.getStartOfMonth(date)
        const monthString = DateService.formatDateForAPI(month)
        const url = this._buildUrl(this.booking.venue!)
        const params: any = {
            num_people: this.booking.pax,
            fields: 'date',
            date: monthString
        }

        if (this.booking.type) {
            params.type = this.booking.type.id
        }

        const requestUrl = url + this.objToParams(params)

        return this.get(requestUrl)
            .then((response) => {
                const payload = response.data.payload
                const dateValues = payload.validation.date.suggestedValues
                let monthAvailability: DateAvailability[] = []

                dateValues.forEach((value: any) => {
                    monthAvailability.push(new DateAvailability(value))
                })

                this.cache.addDataForMonth(month, monthAvailability)

                return monthAvailability
            })
    }

    checkNextAvailableDate(): Promise<UnavailableDate | null> {
        const url = this._buildUrl(this.booking.venue!, 'next-date')
        const requestUrl = url + this.objToParams({})

        return this.get(requestUrl)
            .then((response) => {
                const payload = response.data.payload

                if (payload !== null && UnavailableDate.isValidUnavailableDate(payload)) {
                    return new UnavailableDate(payload)
                }

                return null
            })
    }
}



// WEBPACK FOOTER //
// ./src/services/DateAvailabilityProvider.ts